import React from 'react';
import api from 'controllers/raymanAPI';
import {FilesCtxProvider, FilesCtx} from './files';
import Singletons from 'controllers/Singletons';

function map(item)
{
    return {...item, id: item.procedureID, items: item.items.map((intItem) => intItem.itemID)}
}

async function getData()
{
    let indexed = {};

    let response = await api.procedures.getAll();
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.procedureID] = item
    });
    
    return {array: data, indexed: indexed};
}

const ProceduresCtx = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        create: () => {},
        clone: () => {},
        update: () => {},
        delete: () => {},
    }
});

function ProceduresCtxProvider(props)
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(() => {
        getData().then(values => {
            setData(values);
            setLoading(false);
        });
    }, []);

    const internalUpdate = React.useCallback((procedureID, procedureData) => {
        let tmp = {...data};        
        Object.keys(procedureData).forEach((key) => {
            tmp.indexed[procedureID][key] = procedureData[key];
        })
        setData(tmp);
    }, [data]);

    const internalAdd = React.useCallback(async (procedureID, item) => {
        if(data.indexed[procedureID] !== undefined) return internalUpdate(procedureID, map(item));

        let tmp = {...data};
        item = map(item);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];
        setData(tmp);
    }, [data, internalUpdate]);

    const internalDelete = React.useCallback((procedureID) => {
        let tmp = {...data};
        let item = tmp.indexed[procedureID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);
        delete tmp.indexed[procedureID];

        setData(tmp);

    }, [data]);


    const create = React.useCallback(async (data) => {
        let result = await api.procedures.post(data);
        if(result.ok)
        {
            let item = await result.json();
            internalAdd(item.procedureID, item);
            return item;
        }
        else return null;
    }, [internalAdd]);

    const clone = React.useCallback(async (procedureID) => {
        let result = await api.procedures.clone(procedureID);
        if(result.ok)
        {
            let item = await result.json();
            internalAdd(item.procedureID, item);
            return item;
        }
        else return null;
    }, [internalAdd]);

    const update = React.useCallback(async (procedureID, data) => {
        let result = await api.procedures.updateSingle(procedureID, data);
        if(result.ok) internalUpdate(procedureID, map(await result.json()));

    }, [internalUpdate]);

    const remove = React.useCallback(async (procedureID) => {
        let result = await api.procedures.deleteSingle(procedureID);
        if(result.ok) internalDelete(procedureID);
    }, [internalDelete]);

    React.useEffect( function handleNotifications() {

        const handleCreate = (message) => internalAdd(message.procedureID, message);
        const handleUpdate = (message) => internalUpdate(message.procedureID, map(message));
        const handleDelete = (message) => internalDelete(message.procedureID, message);

        Singletons.NotificationsSocket.on('procedures.created', handleCreate);
        Singletons.NotificationsSocket.on('procedures.updated', handleUpdate);
        Singletons.NotificationsSocket.on('procedures.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket.off('procedures.created', handleCreate);
            Singletons.NotificationsSocket.off('procedures.updated', handleUpdate);
            Singletons.NotificationsSocket.off('procedures.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete]);

    return <ProceduresCtx.Provider value={{loading: loading, data: {...data}, funcs: {create: create, update: update, delete: remove, clone: clone}}} >
        {props.children}
        </ProceduresCtx.Provider>
}


function ProcedureFilesProvider({procedureID, ...props})
{
    // APIParams is built as a react state to avoid triggering changes that fetch the data again on FilesCtxProvider
    // if we build an array on the fly, every rerender the array is different (even with the same values) and triggers a new fetch
    const [params, setParams] = React.useState([procedureID]);

    React.useEffect(function updateApiParams()
    {
        setParams([procedureID]);
    }, [procedureID]);

    return <FilesCtxProvider apiEndpoint={api.procedures.files} apiParams={params}>
            <ProceduresFilesNotificationHandler procedureID={procedureID}>
                {props.children}
            </ProceduresFilesNotificationHandler>
        </FilesCtxProvider>
}

function ProceduresFilesNotificationHandler({procedureID, ...props})
{
    const filesCtx = React.useContext(FilesCtx);
    
    React.useEffect( function handleChatNotifications() {

        const addFile = (message) => {if(message.procedureID === procedureID) filesCtx.funcs.internal.add(message.fileInfo.fileID, message.fileInfo);}
        const deleteFile = (message) => {if(message.procedureID === procedureID) filesCtx.funcs.internal.delete(message.fileID);}
        const refreshFile = (message) => {if(message.procedureID === procedureID) filesCtx.funcs.internal.refresh(message.fileInfo.fileID);}

        Singletons.NotificationsSocket.on('procedures.files.created', addFile);
        Singletons.NotificationsSocket.on('procedures.files.deleted', deleteFile);
        Singletons.NotificationsSocket.on('procedures.files.updated', refreshFile);

        return () => {
            Singletons.NotificationsSocket.off('procedures.files.created', addFile);
            Singletons.NotificationsSocket.off('procedures.files.deleted', deleteFile);
            Singletons.NotificationsSocket.off('procedures.files.updated', refreshFile);
        }
    }, [filesCtx, procedureID]);

    return <>{props.children}</>;

}

export {ProceduresCtx, ProceduresCtxProvider, ProcedureFilesProvider};
export {FilesCtx as ProcedureFilesCtx} from './files';